import type { Getter, Setter } from '@dhmk/zustand-lens';
import type {
  EdgeId,
  EdgeInstance,
  Graph,
  GraphId,
  GraphRef,
  NodeId,
  NodeInstance,
} from '../../components/graph-editor/types/graph';
import type { Draft } from 'immer';
import type {
  GraphHistoryEntry,
  GraphHistoryState,
  GraphsSlice,
  IEdge,
  INode,
} from '.';
import { OptionType } from '@morph-mapper/types';

export interface IGraph {
  node: (nodeId: NodeId) => INode;
  edge: (edgeId: EdgeId) => IEdge;

  get: () => Graph;
  getId: () => GraphId;
  getRef: () => GraphRef;
  setRef: (ref: GraphRef) => void;
  getGraphVersion: () => number;
  getTopologyVersion: () => number;
  bumpGraphVersion: () => void;
  bumpTopologyVersion: () => void;
  getNodeIds: () => NodeId[];
  getNodes: () => Record<NodeId, NodeInstance>;
  getEdges: () => Record<EdgeId, EdgeInstance>;
  getEdge: (edgeId: EdgeId) => EdgeInstance;
  setNodes: (nodes: Record<NodeId, NodeInstance>) => void;
  setEdges: (edges: Record<EdgeId, EdgeInstance>) => void;
  setGraph: (graph: Graph) => void;
  delete: () => void;
  deleteGraph: (graphId: GraphId) => void;
  restoreGraph: (graphId: GraphId) => void;
  getHistory: () => GraphHistoryState;
  setHistory: (history: GraphHistoryState) => void;
  pushHistory: (entry: GraphHistoryEntry) => void;
  undoHistory: () => GraphHistoryEntry | null;
  redoHistory: () => GraphHistoryEntry | null;
  clearHistory: () => void;
  getNodeDerivationCount: (nodeId: NodeId) => number;
  enqueueGraph: (seeds?: NodeId[]) => void;
}

export const createGraphAPI = (
  _set: Setter<Draft<GraphsSlice>>,
  get: Getter<Draft<GraphsSlice>>,
  id: GraphId
): IGraph => {
  return {
    node: (nodeId: NodeId) => {
      return get().node(id, nodeId);
    },
    edge: (edgeId: EdgeId) => {
      return get().edge(id, edgeId);
    },

    get: () => {
      return get().get(id);
    },
    getId: () => {
      return get().getId(id);
    },
    getRef: () => {
      return get().getRef(id);
    },
    setRef: (ref) => {
      get().setRef(id, ref);
    },
    getGraphVersion: () => {
      return get().getGraphVersion(id);
    },
    getTopologyVersion: () => {
      return get().getTopologyVersion(id);
    },
    bumpGraphVersion: () => {
      get().bumpGraphVersion(id);
    },
    bumpTopologyVersion: () => {
      get().bumpTopologyVersion(id);
    },
    getNodeIds: () => {
      return get().getNodeIds(id);
    },
    getNodes: () => {
      return get().getNodes(id);
    },
    getEdges: () => {
      return get().getEdges(id);
    },
    getEdge: (edgeId) => {
      return get().getEdge(id, edgeId);
    },
    setNodes: (nodes) => {
      get().setNodes(id, nodes);
    },
    setEdges: (edges) => {
      get().setEdges(id, edges);
    },
    setGraph: (graph) => {
      get().setGraph(id, graph);
    },
    delete: () => {
      get().delete(id);
    },
    deleteGraph: (graphId) => {
      get().deleteGraph(graphId);
    },
    restoreGraph: (graphId) => {
      get().restoreGraph(graphId);
    },
    getHistory: () => {
      return get().getHistory(id);
    },
    setHistory: (history) => {
      get().setHistory(id, history);
    },
    pushHistory: (entry) => {
      get().pushHistory(id, entry);
    },
    undoHistory: () => {
      return get().undoHistory(id);
    },
    redoHistory: () => {
      return get().redoHistory(id);
    },
    clearHistory: () => {
      get().clearHistory(id);
    },
    getNodeDerivationCount: (nodeId) => {
      const graph = get().get(id);
      const node = graph.nodes[nodeId];
      if (!node) return 0;
      const options = node.options ?? {};

      return Object.values(options).reduce((count, option) => {
        if (option.type !== OptionType.Graph) return count;
        if (typeof option.graphId !== 'string') return count;
        return count + 1;
      }, 0);
    },
    enqueueGraph: (seeds?: NodeId[]) => {
      get().enqueueGraph(id, seeds);
    },
  };
};
